// Productions — API-backed slate analytics joined with venues, campaigns,
// reporting, pipeline statistics and pixel configuration.
//
// Field renames vs. MOCK.PRODUCTIONS:
//   p.title          → p.name
//   p.venue (name)   → resolved via p.venue_id + /venues
//   p.avgPrice       → p.avg_price_cents / 100
//   p.weekGross      → p.week_gross_cents / 100
//   p.stage          → derived from p.tag (ProductionTag binding, lowercase)
//   p.dates          → derived from first/last_event_starts_at
//   p.posterBg/Fg/poster → not on canonical (placeholder color derived from id)

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { PageActions } from '@/components/page-actions';
import { ResponsiveHelp } from '@/components/responsive-help';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo, useState, type ReactNode } from 'react';
import { Icons, ChannelPill, StatusDot, fmt, MoneyCell } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import { campaignKindOf, type CampaignStatusKind } from '@/lib/campaign-status';
import {
  useCampaigns,
  usePipelineStats,
  usePixels,
  useProductions,
  useRunningProductionsSummary,
  useReportingByProduction,
  useVenues,
} from '@/api/hooks';
import { api, imageProxyUrl } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { queryClient } from '@/cache/queryClient';
import { productionsList } from '@/api/resources';
import { useCan } from '@/auth/capabilities';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { fmtDate, fmtDateShort, fmtDayShort } from '@/lib/datetime';
import { venueOptionLabel } from '@/lib/venue-label';
import type {
  Campaign,
  Channel,
  Pixel,
  Production,
  ProductionStat,
  ProductionTag,
  ReportByProductionRow,
  RunningProductionsSummary,
  Venue,
} from '@/api/types';

/** `'api' | 'manual'`: a production's origin, or a venue's seats source. */
type SourceKind = Production['source'];

// Shown wherever a production is exempt from alerting: it stays measured,
// reported and attributed everywhere — only the alert evaluators skip it.
function AlertsExemptBadge() {
  return (
    <span
      className="mono"
      title="Exempt from alerts — still measured, reported, and attributed everywhere; alert rules skip it."
      style={{
        fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700,
        padding: '2px 6px', borderRadius: 4, border: '1px solid var(--line-strong)',
        color: 'var(--ink-3)', whiteSpace: 'nowrap',
      }}
    >
      alerts off
    </span>
  );
}

interface StageMeta { color: string; dot: string; label: string }

const STAGE_OPEN: StageMeta = { color: 'var(--ink-1)', dot: 'var(--ink-2)', label: 'OPEN' };

// Map canonical ProductionTag → display label/color. The uppercase keys are
// legacy spellings a caller may still pass verbatim.
const PRODUCTION_STAGE_META: Partial<Record<string, StageMeta>> = {
  preview:  { color: 'var(--flag)',  dot: 'var(--flag)',  label: 'PREVIEW' },
  new:      { color: 'var(--flag)',  dot: 'var(--flag)',  label: 'NEW' },
  open:     STAGE_OPEN,
  open_run: { color: 'var(--ink-1)', dot: 'var(--ink-2)', label: 'OPEN' },
  hit:      { color: 'var(--good)',  dot: 'var(--good)',  label: 'HIT' },
  critic:   { color: 'var(--good)',  dot: 'var(--good)',  label: 'CRITIC' },
  touring:  { color: 'var(--accent)', dot: 'var(--accent)', label: 'TOURING' },
  closed:   { color: 'var(--ink-3)', dot: 'var(--ink-3)', label: 'CLOSED' },
  ENDED:    { color: 'var(--ink-3)', dot: 'var(--ink-3)', label: 'ENDED' },
  OPEN:     { color: 'var(--ink-1)', dot: 'var(--ink-2)', label: 'OPEN' },
  HIT:      { color: 'var(--good)',  dot: 'var(--good)',  label: 'HIT' },
};

function SourcePill({ source }: { source: SourceKind }) {
  const isManual = source === 'manual';
  const tone = isManual ? 'var(--flag)' : 'var(--good)';
  return (
    <span style={{
      display:'inline-flex',alignItems:'center',gap:6,
      fontFamily:'var(--font-mono)',fontSize:10.5,fontWeight:600,
      letterSpacing:'.06em',textTransform:'uppercase',
      color: tone,
      padding:'2px 8px 2px 7px',
      borderRadius:999,
      border:'1px solid ' + `color-mix(in oklch, ${tone} 35%, var(--line))`,
      background: `color-mix(in oklch, ${tone} 10%, transparent)`,
    }}>
      <span style={{ width:6,height:6,borderRadius:'50%', background: tone }}/>
      {isManual ? 'Manual' : 'API'}
    </span>
  );
}

function ProductionStagePill({ stage }: { stage: string | null | undefined }) {
  const key = String(stage || '').toLowerCase();
  const m = PRODUCTION_STAGE_META[key] || (stage ? PRODUCTION_STAGE_META[stage] : undefined) || STAGE_OPEN;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6,fontFamily:'var(--font-mono)',fontSize:10.5,fontWeight:600,letterSpacing:'.04em',color: m.color}}>
      <span style={{
        width:7,height:7,borderRadius:'50%',background: m.dot,
        boxShadow: key === 'hit' ? '0 0 0 2px color-mix(in oklch, var(--good) 25%, transparent)' : 'none',
      }}/>
      {m.label || String(stage || '').toUpperCase()}
    </span>
  );
}

function ManualBadge() { return null; }

function NotTracked({ tip }: { tip?: string }) {
  return (
    <span
      title={tip || 'Not yet wired in Phase C. A reporting hook will surface this once /reporting roll-ups are joined per production.'}
      className="muted"
      style={{fontStyle:'italic',fontSize:11,letterSpacing:0,textTransform:'none'}}
    >not tracked</span>
  );
}

/** What a poster chip reads: pipeline and events pass their own production rows. */
type PosterProduction = Pick<Production, 'id' | 'name' | 'thumbnail_url'>;

function posterStyleFor(production: PosterProduction | null | undefined) {
  if (!production) return { bg: 'var(--ink-3)', fg: '#fff', initials: '—' };
  const seed = String(production.id || production.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue = seed % 360;
  return {
    bg: `oklch(70% 0.12 ${hue})`,
    fg: '#0b0b0b',
    initials: (production.name || '').split(/\s+/).filter(w => w.length > 1).slice(0,3).map(w => w[0] || '').join('').toUpperCase() || '—',
  };
}

interface PosterChipProps {
  production: PosterProduction | null | undefined;
  size?: number;
}

function PosterChip({ production, size = 32 }: PosterChipProps) {
  const [imgErr, setImgErr] = useState(false);
  const p = posterStyleFor(production);
  const w = size, h = Math.round(size * 1.25);
  const thumb = production?.thumbnail_url;
  // Use the EncorePro poster when present; fall back to the generated
  // initials chip on a missing/broken URL.
  if (thumb && !imgErr) {
    return (
      <img
        src={imageProxyUrl(thumb)}
        alt={production?.name || ''}
        title={production?.name}
        loading="lazy"
        onError={() => setImgErr(true)}
        style={{
          width: w, height: h, flexShrink: 0,
          objectFit: 'cover', display: 'block', borderRadius: 2,
          background: p.bg,
          boxShadow: 'inset 0 0 0 1px color-mix(in oklch, ' + (p.fg) + ' 20%, transparent)',
        }}
      />
    );
  }
  return (
    <div style={{
      width: w, height: h, flexShrink: 0,
      background: p.bg, color: p.fg,
      display:'grid', placeItems:'center',
      borderRadius: 2,
      fontFamily:'var(--font-display)', fontWeight: 900, fontSize: Math.round(size / 3),
      lineHeight: 0.95, textAlign:'center', letterSpacing:'.02em',
      boxShadow: 'inset 0 0 0 1px color-mix(in oklch, ' + (p.fg) + ' 20%, transparent)',
    }} title={production?.name}>
      {p.initials}
    </div>
  );
}

const CHANNEL_GLYPH_ORDER: readonly Channel[] = ['meta', 'ctv', 'dsp'];

function ChannelGlyphs({ channels }: { channels: readonly Channel[] | null | undefined }) {
  if (!channels?.length) return <span className="muted mono" style={{fontSize:10}}>—</span>;
  const meta: Record<Channel, { label: string }> = { meta: { label: 'M' }, ctv: { label: 'C' }, dsp: { label: 'D' } };
  return (
    <span style={{display:'inline-flex',gap:3}}>
      {CHANNEL_GLYPH_ORDER.map(ch => {
        const on = channels.includes(ch);
        const m = meta[ch];
        return (
          <span key={ch} title={ch.toUpperCase()} style={{
            fontFamily:'var(--font-mono)',fontSize:9.5,fontWeight:700,
            width:16,height:16,display:'grid',placeItems:'center',
            borderRadius:2,
            background: on ? 'color-mix(in oklch, var(--good) 15%, transparent)' : 'transparent',
            color: on ? 'var(--good)' : 'var(--ink-3)',
            border: '1px solid ' + (on ? 'color-mix(in oklch, var(--good) 35%, transparent)' : 'var(--line)'),
            opacity: on ? 1 : 0.4,
          }}>{m.label}</span>
        );
      })}
    </span>
  );
}

function HeatBar({ heat }: { heat: number }) {
  const c = heat >= 85 ? 'var(--good)' : heat >= 65 ? 'var(--ink-1)' : heat >= 45 ? 'var(--warn)' : 'var(--bad)';
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,minWidth:90}}>
      <div style={{flex:1,height:4,background:'var(--bg-sunk)',borderRadius:1,overflow:'hidden'}}>
        <div style={{width: heat + '%',height:'100%',background: c}}/>
      </div>
      <span className="mono num" style={{fontSize:11,fontWeight:600,width:24,color:c,textAlign:'right'}}>{heat}</span>
    </div>
  );
}

interface ProductionsKpiStripProps {
  productions: readonly Production[];
  campaigns: readonly Campaign[];
  roasRows?: readonly ReportByProductionRow[];
  runningSummary: RunningProductionsSummary | undefined;
}

/** One KPI cell: label, value, optional sub-line, and whether the sub-line is a rich block. */
type KpiCell = [label: string, value: ReactNode, sub?: ReactNode, isRich?: boolean];

function ProductionsKpiStrip({ productions, campaigns, roasRows = [], runningSummary }: ProductionsKpiStripProps) {
  const apiShows    = productions.filter(s => s.source === 'api');
  const manualShows = productions.filter(s => s.source === 'manual');
  const isRunning = (s: Production) => !['closed', 'ENDED'].includes(s.tag || '');
  const canonicalRunningIds = runningSummary
    ? new Set(runningSummary.production_ids)
    : null;
  const apiRunning = canonicalRunningIds
    ? apiShows.filter(s => canonicalRunningIds.has(s.id))
    : apiShows.filter(isRunning);
  const manualRunning = canonicalRunningIds
    ? manualShows.filter(s => canonicalRunningIds.has(s.id))
    : manualShows.filter(isRunning);
  const running = [...apiRunning, ...manualRunning];
  const totalRunning = runningSummary ? Number(runningSummary.total) : null;
  const apiRunningCount = runningSummary ? Number(runningSummary.api) : apiRunning.length;
  const manualRunningCount = runningSummary ? Number(runningSummary.manual) : manualRunning.length;
  const apiPct = totalRunning ? (apiRunningCount / totalRunning) * 100 : 0;

  // Total 7-day box office over ALL productions (matches the Venues "Week gross"
  // total; manual productions carry null gross → contribute 0). The other cells
  // below stay running-scoped.
  const weekGross = productions.reduce((a, s) => a + ((s.week_gross_cents || 0) / 100), 0);
  // Active campaigns derived from /campaigns list — count campaigns tied to running productions.
  const runningIds = new Set(running.map(s => s.id));
  const activeCampaigns = campaigns.filter(c => campaignKindOf(c) === 'live' && runningIds.has(c.production_id)).length;
  const spend30 = campaigns.filter(c => runningIds.has(c.production_id)).reduce((a, c) => a + ((c.spent_cents || 0) / 100), 0);
  // Spend-weighted portfolio ROAS = Σ attributed revenue ÷ Σ ad spend across running productions.
  const runningRoas = roasRows.filter(r => runningIds.has(r.production_id));
  const portSpend = runningRoas.reduce((a, r) => a + (Number(r.spend_cents) || 0), 0);
  const portRev   = runningRoas.reduce((a, r) => a + (Number(r.revenue_cents) || 0), 0);
  const portfolioRoas = portSpend > 0 ? portRev / portSpend : null;
  const apiOnly = <span className="muted mono" style={{fontSize:9.5,letterSpacing:'.06em'}}>API productions only</span>;

  const productionsRunningSub = (
    <>
      <div style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2,display:'flex',justifyContent:'space-between'}}>
        <span><span style={{color:'var(--accent)'}}>● </span>{apiRunningCount} API</span>
        <span><span style={{color:'var(--flag)'}}>● </span>{manualRunningCount} manual</span>
      </div>
      <div style={{display:'flex',height:5,background:'var(--bg-sunk)',borderRadius:1,overflow:'hidden',marginTop:4}}>
        <div style={{width: apiPct + '%',background:'var(--accent)'}}/>
        <div style={{width: (100 - apiPct) + '%',background:'var(--flag)'}}/>
      </div>
    </>
  );

  // Portfolio avg fill = weighted Σ sold ÷ Σ capacity over running API productions'
  // seated events (same method as Events + Venues). Manual productions have no
  // seated events → null sums → contribute 0.
  const capSoldSum = apiRunning.reduce((a, s) => a + (s.seated_sold || 0), 0);
  const capSeatSum = apiRunning.reduce((a, s) => a + (s.seated_capacity || 0), 0);
  const avgCapacity = capSeatSum > 0 ? capSoldSum / capSeatSum : null;

  const cells: KpiCell[] = [
    ['Productions running',   totalRunning == null ? '—' : fmt.nKpi(totalRunning),  productionsRunningSub, true],
    ['Avg capacity',          avgCapacity != null ? Math.round(avgCapacity * 100) + '%' : '—', apiOnly],
    ['Active campaigns',      fmt.nKpi(activeCampaigns)],
    ['Ad spend',              spend30 ? <MoneyCell value={spend30} dp={2}/> : '—'],
    ['Portfolio ROAS',        portfolioRoas != null ? portfolioRoas.toFixed(2) + '×' : '—'],
    ['Week gross',            weekGross ? <MoneyCell value={weekGross} dp={2}/> : '—'],
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip" style={{gridTemplateColumns:'repeat(6, 1fr)'}}>
      {cells.map(([k, v, sub, isRich]) => (
        <div className="kpi" key={k}>
          <div className="kpi-label">{k}</div>
          <div className="kpi-val">{v}</div>
          {sub && !isRich && <div className="kpi-sub" style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
          {sub && isRich && sub}
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

function SignalHealthPill({ tone = 'neutral', label }: { tone?: 'ok' | 'bad' | 'neutral'; label: string }) {
  const color = tone === 'ok' ? 'var(--good)' : tone === 'bad' ? 'var(--bad)' : 'var(--ink-3)';
  return (
    <span style={{
      display:'inline-flex',alignItems:'center',gap:6,
      padding:'4px 10px',borderRadius:'var(--r-sm, 4px)',
      background: `color-mix(in oklch, ${color} 12%, transparent)`,
      border: '1px solid ' + (tone === 'neutral' ? 'var(--line)' : `color-mix(in oklch, ${color} 30%, transparent)`),
      fontFamily:'var(--font-mono)',fontSize:10.5,letterSpacing:'.04em',fontWeight:600,
      color,
    }}>
      <span style={{width:6,height:6,borderRadius:'50%',background: color}}/>
      {label}
    </span>
  );
}

// The drawer lists every campaign of the production, so it orders them by
// where they are in their life: live first, then drafts, then ended, each group
// in the order the list already had. The group comes from the same kind the
// Status column renders (`campaignKindOf`). "Live" is exactly the `live` kind,
// the one the "N live" counter counts. "Draft" is every kind that has not
// launched yet: `draft`, `armed` (a draft queued to auto-launch) and
// `ready_to_publish`. Every other kind (paused, pausing, paused by system,
// scheduled, starting, partially active, needs attention, unverified) is a
// launched campaign that is not over, the ones an operator is most likely to
// act on, so it sits right after the live ones, again in list order.
const CAMPAIGN_GROUP_ORDER: Partial<Record<CampaignStatusKind, number>> = {
  live: 0,
  draft: 2,
  armed: 2,
  ready_to_publish: 2,
  ended: 3,
};
const OTHER_CAMPAIGN_GROUP = 1;

function orderDrawerCampaigns(campaigns: readonly Campaign[]): Campaign[] {
  return campaigns
    .map((campaign, index) => ({ campaign, index, group: CAMPAIGN_GROUP_ORDER[campaignKindOf(campaign)] ?? OTHER_CAMPAIGN_GROUP }))
    .sort((a, b) => a.group - b.group || a.index - b.index)
    .map(({ campaign }) => campaign);
}

interface ProductionDetailDrawerProps {
  production: Production | null | undefined;
  venue: Venue | null | undefined;
  roas: number | null | undefined;
  campaigns: readonly Campaign[];
  stat: ProductionStat | null | undefined;
  pixel: Pixel | null | undefined;
  onClose: () => void;
  setView?: (viewId: string) => void;
  canEdit?: boolean;
  onExemptToggle?: ((id: Production['id'], alertsExempt: boolean) => void) | null;
  exemptSaving?: boolean;
  exemptError?: string | null;
}

function ProductionDetailDrawer({ production, venue, roas, campaigns, stat, pixel, onClose, setView: _setView, canEdit = false, onExemptToggle = null, exemptSaving = false, exemptError = null }: ProductionDetailDrawerProps) {
  if (!production) return null;
  const spend30 = campaigns.reduce((a, c) => a + ((c.spent_cents || 0) / 100), 0);
  const runDates = (() => {
    // first/last_event_starts_at are instants → render in NY.
    const a = production.first_event_starts_at ? fmtDateShort(production.first_event_starts_at) : '—';
    const b = production.last_event_starts_at  ? fmtDate(production.last_event_starts_at) : '—';
    return `${a} – ${b}`;
  })();
  return (
    <>

      <ResponsiveOverlay label="productions details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1,minWidth:0,display:'flex',gap:14}}>
            <PosterChip production={production} size={56}/>
            <div style={{minWidth:0,flex:1}}>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Production · <span style={{color:'var(--ink-1)'}}>{production.id}</span></div>
              <h2 style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,letterSpacing:'.01em',margin:'4px 0 6px',textTransform:'uppercase'}}>{production.name}</h2>
              <div className="flex items-center gap-2" style={{flexWrap:'wrap'}}>
                <ProductionStagePill stage={production.tag}/>
                <SourcePill source={production.source}/>
                {production.alerts_exempt && <AlertsExemptBadge/>}
                <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>{venue?.name || production.venue_id || '—'} · {runDates}</span>
              </div>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          {production.source === 'manual' && (
            <div className="panel" style={{
              padding:'10px 12px',marginBottom:14,
              background:'color-mix(in oklch, var(--flag) 8%, transparent)',
              borderLeft:'3px solid var(--flag)',
              fontSize:11.5,color:'var(--ink-1)',
            }}>
              <span className="mono" style={{fontSize:10,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--flag)',fontWeight:700}}>Manually added</span>
              <span style={{marginLeft:8}}>This production was added by hand. Box-office telemetry isn't available.</span>
            </div>
          )}

          <div className="field-lbl mb-2">At a glance</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="production-glance" style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:14}}>
              {([
                ['Venue seats',   venue?.seats != null ? venue.seats.toLocaleString() : <NotTracked/>],
                ['Week gross',    production.week_gross_cents != null ? <MoneyCell value={production.week_gross_cents / 100}/> : <NotTracked/>],
                ['Avg ticket',    production.avg_price_cents != null ? <MoneyCell value={production.avg_price_cents / 100}/> : <NotTracked/>],
                ['Heat',          production.heat == null ? <NotTracked/> : <HeatBar heat={production.heat}/>, production.genre || ''],
                ['ROAS',          roas != null && isFinite(roas) && roas > 0 ? <span>{roas.toFixed(2)}×</span> : <span className="muted">—</span>],
                ['Ad spend',      spend30 > 0 ? <MoneyCell value={spend30}/> : <span className="muted">—</span>],
                ['Run',           runDates, production.tag ? production.tag.toUpperCase() : ''],
              ] satisfies Array<[label: string, value: ReactNode, sub?: string]>).map(([k,v,sub]) => (
                <div key={k}>
                  <div className="muted mono" style={{fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase'}}>{k}</div>
                  <div className="mono" style={{fontSize:18,fontWeight:700,marginTop:2}}>{v}</div>
                  {sub && <div className="mono" style={{fontSize:10.5,color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
                </div>
              ))}
            </div>
          </div>

          <div className="field-lbl mb-2 flex between items-center">
            <span>Campaigns</span>
            <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>{campaigns.filter(c => campaignKindOf(c) === 'live').length} live · {campaigns.length} total</span>
          </div>
          <div className="panel" style={{padding:0,marginBottom:14,overflow:'hidden'}}>
            {campaigns.length === 0 ? (
              <div className="muted" style={{padding:18,textAlign:'center',fontSize:11.5}}>No campaigns for this production yet.</div>
            ) : (
              <table className="tbl" style={{margin:0}}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th className="num">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDrawerCampaigns(campaigns).map(c => (
                    <tr key={c.id} style={{cursor:'default'}}>
                      <td style={{whiteSpace:'normal',maxWidth:240}}>{c.name}</td>
                      <td><ChannelPill channel={c.channel}/></td>
                      <td><StatusDot s={campaignKindOf(c)}/></td>
                      <td className="num"><MoneyCell value={(c.spent_cents || 0) / 100}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="field-lbl mb-2">Signal health</div>
          <div className="panel" style={{padding:12,marginBottom:14,display:'flex',flexDirection:'column',gap:8}}>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              <SignalHealthPill tone={pixel ? 'ok' : 'neutral'} label={pixel ? `PIXEL · ${pixel.id}` : 'PIXEL · not configured'}/>
              <SignalHealthPill
                tone="neutral"
                label={stat && stat.tickets > 0 && stat.last_active_day != null
                  ? `INGEST · LAST ACTIVE ${fmtDayShort(stat.last_active_day)}`
                  : 'INGEST · no tickets · 30d'}
              />
            </div>
            {stat && stat.tickets > 0 && (
              <div style={{display:'flex',gap:18,fontSize:11,color:'var(--ink-2)',paddingTop:6,borderTop:'1px solid var(--line-2)'}}>
                <span><b style={{color:'var(--ink-1)'}}>{fmt.n(stat.tickets || 0)}</b> tickets / 30d</span>
              </div>
            )}
          </div>
        </div>

        {canEdit && onExemptToggle && (
          <div style={{padding:'12px 20px',borderTop:'1px solid var(--line)'}}>
            <label style={{display:'inline-flex',alignItems:'center',gap:8,fontSize:12,cursor:exemptSaving?'default':'pointer'}}>
              <input
                type="checkbox"
                checked={!!production.alerts_exempt}
                disabled={exemptSaving}
                onChange={(e) => onExemptToggle(production.id, e.target.checked)}
              />
              Exempt from alerts {exemptSaving && <span role="status">Saving…</span>}
            </label>
            <ResponsiveHelp label="About alert exemptions">
            <div className="field-hint" style={{marginTop:4}}>
              Still measured, reported, and attributed everywhere — alert rules skip this
              production (its data quality is broken by design, e.g. a fee bucket or
              rehearsal artifact). Its open alerts auto-resolve on the next scan, and it
              leaves the System Health quality bands.
            </div>
            </ResponsiveHelp>
            {exemptError && (
              <div className="field-hint" role="alert" style={{marginTop:4,color:'var(--bad)'}}>
                Update failed — the setting was not saved: {exemptError}
              </div>
            )}
          </div>
        )}
        <div className="drawer-foot">
          <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>Overview · single-pane</span>
          <button className="btn btn-sm" style={{marginLeft:'auto'}} onClick={onClose}>Close</button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// Manual production creation was removed deliberately: the slate mirrors
// EncorePro (nightly + on-demand backfill upserts; operator-set columns preserved)
// and there is no POST /productions route. Restore the modal from git history
// alongside the server route if manual creates return.

// Declared once: the widths, the headers and the empty-state colSpan all come
// from here, so a column can no longer be added to one and forgotten in another.
const ROW_H = 56;
const { Table } = createDataTable([
  { key: 'name',   header: 'Production',      width: 300, mobile: 'title' },
  { key: 'source', header: 'Source',          width: 100, priority: 2, mobile: 'stat' },
  { key: 'stage',  header: 'Stage',           width: 120, mobile: 'status' },
  { key: 'run',    header: 'Run',             width: 200, mobile: 'stat' },
  { key: 'seats',  header: 'Venue seats',     width: 160, priority: 3,
    title: "Physical seats configured on the production's venue" },
  { key: 'gross',  header: 'Week gross',      width: 180, priority: 2, mobile: 'stat' },
  { key: 'avg',    header: 'Avg ticket',      width: 100, align: 'num', priority: 3 },
  { key: 'camps',  header: 'Campaigns',       width: 160, priority: 2 },
  { key: 'spend',  header: 'Ad spend · 30d',  width: 180, align: 'num', mobile: 'stat' },
  { key: 'roas',   header: 'ROAS',            width: 100, align: 'num', priority: 2 },
  { key: 'heat',   header: 'Heat',            width: 130, priority: 3 },
]);

/** A production with no venue has no venue row, rather than looking up the key "null". */
function venueFor(venueMap: Readonly<Record<string, Venue>>, venueId: string | null): Venue | undefined {
  return venueId != null ? venueMap[venueId] : undefined;
}

type StageFilter = ProductionTag | 'all';
type SourceFilter = SourceKind | 'all';

function ProductionsView({ setView }: { setView?: (viewId: string) => void }) {
  const productionsQuery = useProductions();
  const productions = productionsQuery.data ?? [];
  const { data: runningSummary } = useRunningProductionsSummary();
  const { data: venues = [] } = useVenues();
  const { data: campaigns = [] } = useCampaigns();
  const { data: pstats = [] } = usePipelineStats();
  const statById = useMemo(() => Object.fromEntries(pstats.map(s => [s.production_id, s])), [pstats]);
  const { data: pixels = [] } = usePixels();
  // Attributed ROAS per production (revenue ÷ ad spend) — last30 by default.
  const { data: roasRows = [] } = useReportingByProduction();
  const canEdit = useCan('backend_edit', 'own');

  // The one operator write on this screen: the alerts exemption, audited
  // server-side (mirrors the venues seats override).
  const exemptM = useRegisteredMutation({
    domains: ['catalog'],
    mutationFn: ({ id, alertsExempt }: { id: Production['id']; alertsExempt: boolean }) =>
      api.patch(`/productions/${encodeURIComponent(id)}`, { body: { alerts_exempt: alertsExempt } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productionsList.all }),
  });
  const onExemptToggle = (id: Production['id'], alertsExempt: boolean) => exemptM.mutate({ id, alertsExempt });

  const venueMap = useMemo(() => Object.fromEntries(venues.map(v => [v.id, v])), [venues]);
  const roasByProduction = useMemo(
    () => Object.fromEntries(roasRows.map(r => [r.production_id, r.roas])),
    [roasRows],
  );
  const campaignsByProduction = useMemo(() => {
    const out = new Map<Campaign['production_id'], Campaign[]>();
    for (const c of campaigns) {
      if (!c.production_id) continue;
      const list = out.get(c.production_id);
      if (list) list.push(c);
      else out.set(c.production_id, [c]);
    }
    return out;
  }, [campaigns]);

  const [stageFilter, setStageFilter]   = useState<StageFilter>('all');
  const [venueFilter, setVenueFilter]   = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [q, setQ]                       = useState('');
  const [selected, setSelected]         = useState<Production | null>(null);

  const sorted = useMemo(() => [...productions].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [productions]);

  const rows = sorted.filter(s => {
    if (stageFilter !== 'all' && s.tag !== stageFilter) return false;
    if (venueFilter !== 'all' && s.venue_id !== venueFilter) return false;
    if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      const venueName = venueFor(venueMap, s.venue_id)?.name || '';
      const hay = [s.name, venueName, s.genre].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });


  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Productions</h1>
        </div>
        <PageActions>
          <span className="muted mono" data-phone="hidden" title="The production slate mirrors EncorePro (nightly + on-demand backfill); operator-set columns like tag and heat are preserved across syncs." style={{fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', alignSelf:'center', marginRight:4}}>
            ⟳ Synced from EncorePro
          </span>
          <button className="btn btn-icon-phone" data-phone="inline" aria-label="Export CSV" onClick={() => downloadCsv(csvFilename('productions'), rows.map(s2 => {
            const stat = statById[s2.id];
            const prodCampaigns = campaignsByProduction.get(s2.id) || [];
            return {
              'Production': s2.name || '',
              'ID': s2.id,
              'Source': s2.source || 'api',
              'Stage': s2.tag || '',
              'Genre': s2.genre || '',
              'Venue': venueFor(venueMap, s2.venue_id)?.name || s2.venue_id || '',
              'First event': s2.first_event_starts_at || '',
              'Last event': s2.last_event_starts_at || '',
              'Avg price $': s2.avg_price_cents != null ? (s2.avg_price_cents / 100).toFixed(2) : '',
              'Week gross $': s2.week_gross_cents != null ? (s2.week_gross_cents / 100).toFixed(2) : '',
              'Tickets 30d': stat?.tickets ?? '',
              'Revenue 30d $': stat?.revenue_cents != null ? (stat.revenue_cents / 100).toFixed(2) : '',
              'Live campaigns': prodCampaigns.filter(c => campaignKindOf(c) === 'live').length,
              'ROAS 30d': roasByProduction[s2.id] ?? '',
              'Heat': s2.heat ?? '',
              'Alerts exempt': s2.alerts_exempt ? 'yes' : '',
            };
          }))}><span>{Icons.dl}</span><span className="phone-long">Export CSV</span></button>
        </PageActions>
      </div>

      <ProductionsKpiStrip
        productions={productions}
        campaigns={campaigns}
        roasRows={roasRows}
        runningSummary={runningSummary}
      />

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0'}}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter productions by title, venue, or genre"/>
        </div>
        <Dropdown value={stageFilter} options={[
          { id:'all',      label:'All stages' },
          { id:'preview',  label:'Preview' },
          { id:'new',      label:'New' },
          { id:'open',     label:'Open' },
          { id:'open_run', label:'Open run' },
          { id:'hit',      label:'Hit' },
          { id:'critic',   label:'Critic' },
          { id:'touring',  label:'Touring' },
          { id:'closed',   label:'Closed' },
        ]} onChange={setStageFilter} label="Stage" width={180}/>
        <Dropdown value={venueFilter} options={[
          { id:'all', label:'All venues' },
          ...venues.map(v => ({ id: v.id, label: venueOptionLabel(v) })),
        ]} onChange={setVenueFilter} label="Venue" width={200}/>
        <Dropdown value={sourceFilter} options={[
          { id:'all',    label:'All sources' },
          { id:'api',    label:'API-synced' },
          { id:'manual', label:'Manually added' },
        ]} onChange={setSourceFilter} label="Source"/>
      </ResponsiveFilters>

      <Table
        rows={rows}
        rowH={ROW_H}
        unfilteredTotal={productions.length}
        deps={[{ what: 'productions', query: productionsQuery }]}
        empty="No productions yet. They sync in from EncorePro automatically."
        noMatch="No productions match these filters."
        onRowClick={setSelected}
        renderRow={(s) => {
          const v = venueFor(venueMap, s.venue_id);
          const prodCampaigns = campaignsByProduction.get(s.id) || [];
          const activeCount = prodCampaigns.filter(c => campaignKindOf(c) === 'live').length;
          const channels = [...new Set(prodCampaigns.map(c => c.channel))];
          const spend30 = prodCampaigns.reduce((a, c) => a + ((c.spent_cents || 0) / 100), 0);
          const roas = roasByProduction[s.id];
          const runDate = s.first_event_starts_at ? fmtDateShort(s.first_event_starts_at) : '—'; // NY instant
          const endDate = s.last_event_starts_at  ? fmtDate(s.last_event_starts_at) : '—'; // NY instant
          return (
          <>
            <td className="name" style={{lineHeight:1.25}}>
              <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0,flex:1}}>
                <PosterChip production={s} size={32}/>
                <div style={{minWidth:0,flex:1,overflow:'hidden'}}>
                  <div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.name}</div>
                  <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',fontWeight:400,marginTop:2}}>{v?.name || s.venue_id || '—'}{s.genre ? ' · ' + s.genre : ''}</div>
                </div>
              </div>
            </td>
            <td><SourcePill source={s.source}/></td>
            <td>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <ProductionStagePill stage={s.tag}/>
                {s.alerts_exempt && <AlertsExemptBadge/>}
              </div>
            </td>
            <td className="mono" style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.2}}>
              <div style={{display:'flex',flexDirection:'column',gap:1}}>
                <span>{runDate} – {endDate}</span>
              </div>
            </td>
            <td className="num mono">{v?.seats != null ? v.seats.toLocaleString() : <NotTracked/>}</td>
            <td className="num">{s.week_gross_cents != null ? <MoneyCell value={s.week_gross_cents / 100}/> : <NotTracked/>}</td>
            <td className="num">{s.avg_price_cents != null ? <MoneyCell value={s.avg_price_cents / 100}/> : <NotTracked/>}</td>
            <td>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span className="mono num" style={{fontSize:12,fontWeight:700,minWidth:14,textAlign:'right'}}>{activeCount}</span>
                <ChannelGlyphs channels={channels}/>
              </div>
            </td>
            <td className="num">
              {spend30 > 0 ? <MoneyCell value={spend30}/> : <span className="muted">—</span>}
            </td>
            <td className="num mono">{roas != null && isFinite(roas) && roas > 0 ? <span>{roas.toFixed(2)}×</span> : <span className="muted">—</span>}</td>
            <td>{s.heat == null ? <NotTracked/> : <HeatBar heat={s.heat}/>}</td>
          </>
          );
        }}
      />

      {selected && (
        <ProductionDetailDrawer
          production={productions.find((p) => p.id === selected.id) ?? selected}
          venue={venueFor(venueMap, selected.venue_id)}
          roas={roasByProduction[selected.id]}
          campaigns={campaignsByProduction.get(selected.id) || []}
          stat={statById[selected.id]}
          pixel={pixels.find(p => p.default_production_id && p.default_production_id === selected.id)}
          onClose={() => { exemptM.reset(); setSelected(null); }}
          setView={setView}
          canEdit={canEdit}
          onExemptToggle={onExemptToggle}
          exemptSaving={exemptM.isPending}
          exemptError={exemptM.isError ? String(exemptM.error?.message ?? exemptM.error) : null}
        />
      )}
    </div>
  );
}

export { ProductionsView, ProductionDetailDrawer, ProductionStagePill, PosterChip, ChannelGlyphs, ManualBadge, SourcePill, AlertsExemptBadge };
